import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useParams } from '@tanstack/react-router'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useFeedback } from '@/components/feedback/feedback-context'
import { InfoNotice } from '@/components/feedback/info-notice'
import { FormField } from '@/components/forms/form-field'
import { Input } from '@/components/forms/input'
import { Switch } from '@/components/forms/switch'
import { Textarea } from '@/components/forms/textarea'
import { PageContentBoundary } from '@/components/layout/page-content-boundary'
import { PageHeader } from '@/components/layout/page-header'
import { Breadcrumbs } from '@/components/navigation/breadcrumbs'
import { Button } from '@/components/primitives/button'
import { AdminShellPage } from '@/features/admin/shell/shell-page'

import {
  createOidcProtocolMapper,
  deleteOidcProtocolMapper,
  getOidcClientScope,
  listOidcClaimSources,
  updateOidcClientScope,
  updateOidcProtocolMapper,
  type OidcClientScopePayload,
} from './api'
import { IdpClientScopeTabs, type IdpClientScopeDetailTab } from './idp-client-scope-tabs'
import { OidcProtocolMapperTable } from './idp-protocol-mapper-table'
import { isOidcIdpReadOnly } from './idp-permissions'

export function AdminOidcClientScopeDetailPage() {
  const { t } = useTranslation('adminIdp')
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { showFeedback } = useFeedback()
  const readOnly = isOidcIdpReadOnly()
  const params = useParams({ strict: false })
  const clientScopeId = params.clientScopeId ?? ''
  const [tab, setTab] = useState<IdpClientScopeDetailTab>('mappers')
  const contentClassName = tab === 'settings' ? 'max-w-[720px]' : 'max-w-[1120px]'
  const [settingsDraft, setSettingsDraft] = useState<{
    scopeId: string
    values: Partial<OidcClientScopePayload>
  } | null>(null)
  const detailQueryKey = ['admin-idp-client-scope', clientScopeId]
  const detailQuery = useQuery({
    queryFn: () => getOidcClientScope(clientScopeId),
    queryKey: detailQueryKey,
  })
  const sourcesQuery = useQuery({
    queryFn: listOidcClaimSources,
    queryKey: ['admin-idp-claim-sources'],
  })

  const refreshDetail = () => {
    void queryClient.invalidateQueries({ queryKey: detailQueryKey })
    void queryClient.invalidateQueries({ queryKey: ['admin-idp-client-scopes'] })
  }
  const updateScopeMutation = useMutation({
    mutationFn: updateOidcClientScope,
    onError: () => showFeedback({ message: t('clientScopes.errors.update'), tone: 'error' }),
    onSuccess: () => {
      setSettingsDraft(null)
      refreshDetail()
      showFeedback({ message: t('clientScopes.success.updated'), tone: 'success' })
    },
  })
  const createMapperMutation = useMutation({
    mutationFn: createOidcProtocolMapper,
    onError: () => showFeedback({ message: t('mappers.errors.create'), tone: 'error' }),
    onSuccess: () => {
      refreshDetail()
      showFeedback({ message: t('mappers.success.created'), tone: 'success' })
    },
  })
  const updateMapperMutation = useMutation({
    mutationFn: updateOidcProtocolMapper,
    onError: () => showFeedback({ message: t('mappers.errors.update'), tone: 'error' }),
    onSuccess: () => {
      refreshDetail()
      showFeedback({ message: t('mappers.success.updated'), tone: 'success' })
    },
  })
  const deleteMapperMutation = useMutation({
    mutationFn: deleteOidcProtocolMapper,
    onError: () => showFeedback({ message: t('mappers.errors.delete'), tone: 'error' }),
    onSuccess: () => {
      refreshDetail()
      showFeedback({ message: t('mappers.success.deleted'), tone: 'success' })
    },
  })

  const scope = detailQuery.data
  const currentSettingsDraft = settingsDraft?.scopeId !== clientScopeId ? {} : settingsDraft.values
  const settings = scope
    ? {
        description: scope.description,
        display_name: scope.display_name,
        enabled: scope.enabled,
        include_in_token_scope: scope.include_in_token_scope,
        name: scope.name,
        ...currentSettingsDraft,
      }
    : null
  const updateSettings = (values: Partial<OidcClientScopePayload>) =>
    setSettingsDraft((current) => ({
      scopeId: clientScopeId,
      values: {
        ...(current?.scopeId !== clientScopeId ? {} : current.values),
        ...values,
      },
    }))
  return (
    <AdminShellPage activePath="/admin/idp/client-scopes">
      <div className="flex h-full flex-col overflow-hidden bg-white">
        <PageHeader
          breadcrumbs={
            <Breadcrumbs
              ariaLabel={t('breadcrumbs.label')}
              items={[
                {
                  href: '/admin/idp/client-scopes',
                  label: t('clientScopes.title'),
                  onNavigate: () => void navigate({ to: '/admin/idp/client-scopes' }),
                  title: t('actions.backToClientScopes'),
                },
                {
                  label: scope?.display_name ?? t('clientScopes.detailTitle'),
                },
              ]}
              variant="pageHeader"
            />
          }
          className="shrink-0"
          contentClassName={contentClassName}
        />
        <IdpClientScopeTabs
          activeTab={tab}
          contentClassName={contentClassName}
          onTabChange={setTab}
        />
        <div
          aria-labelledby={`oidc-client-scope-${tab}-tab`}
          className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 pt-6 pb-5 lg:px-[30px]"
          id={`oidc-client-scope-${tab}-panel`}
          role="tabpanel"
        >
          <PageContentBoundary className={contentClassName}>
            {detailQuery.isError ? (
              <p className="text-sm text-[var(--color-danger)]">
                {t('clientScopes.errors.loadDetail')}
              </p>
            ) : detailQuery.isLoading || !scope || !settings ? (
              <div className="h-32 animate-pulse rounded-lg bg-[var(--color-surface-muted)]" />
            ) : tab === 'settings' ? (
              <div className="grid gap-5">
                {scope.system ? (
                  <InfoNotice title={t('clientScopes.builtInReadOnlyTitle')}>
                    {t('clientScopes.builtInReadOnly')}
                  </InfoNotice>
                ) : null}
                <FormField label={t('clientScopes.fields.name')} required>
                  <Input
                    disabled={scope.system || readOnly}
                    onChange={(event) => updateSettings({ name: event.target.value })}
                    value={settings.name}
                  />
                </FormField>
                <FormField label={t('clientScopes.fields.displayName')} required>
                  <Input
                    disabled={scope.system || readOnly}
                    onChange={(event) => updateSettings({ display_name: event.target.value })}
                    value={settings.display_name}
                  />
                </FormField>
                <FormField label={t('clientScopes.fields.description')}>
                  <Textarea
                    disabled={scope.system || readOnly}
                    onChange={(event) => updateSettings({ description: event.target.value })}
                    value={settings.description}
                  />
                </FormField>
                <div className="divide-y divide-[var(--color-divider)] border-y border-[var(--color-divider)]">
                  <label className="flex items-start justify-between gap-5 py-4">
                    <span>
                      <span className="block text-sm font-medium">
                        {t('clientScopes.fields.includeInTokenScope')}
                      </span>
                      <span className="mt-1 block text-xs text-[var(--color-text-muted)]">
                        {t('clientScopes.fields.includeInTokenScopeHint')}
                      </span>
                    </span>
                    <Switch
                      checked={settings.include_in_token_scope}
                      disabled={scope.system || readOnly}
                      onCheckedChange={(include_in_token_scope) =>
                        updateSettings({ include_in_token_scope })
                      }
                    />
                  </label>
                  <label className="flex items-center justify-between gap-5 py-4">
                    <span className="text-sm font-medium">{t('clientScopes.fields.enabled')}</span>
                    <Switch
                      checked={settings.enabled}
                      disabled={scope.system || readOnly}
                      onCheckedChange={(enabled) => updateSettings({ enabled })}
                    />
                  </label>
                </div>
                {scope.system || readOnly ? null : (
                  <div>
                    <Button
                      isLoading={updateScopeMutation.isPending}
                      onClick={() =>
                        updateScopeMutation.mutate({ clientScopeId, payload: settings })
                      }
                    >
                      {t('actions.save')}
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <OidcProtocolMapperTable
                deleting={deleteMapperMutation.isPending}
                mappers={scope.mappers}
                onCreate={(payload) => createMapperMutation.mutateAsync({ clientScopeId, payload })}
                onDelete={(mapper) =>
                  deleteMapperMutation.mutate({ clientScopeId, mapperId: mapper.id })
                }
                onUpdate={(mapperId, payload) =>
                  updateMapperMutation.mutateAsync({ clientScopeId, mapperId, payload })
                }
                readOnly={scope.system || readOnly}
                saving={createMapperMutation.isPending || updateMapperMutation.isPending}
                sources={sourcesQuery.data ?? []}
              />
            )}
          </PageContentBoundary>
        </div>
      </div>
    </AdminShellPage>
  )
}
